import * as React from 'react';

import { redirect } from 'next/navigation';
import { Auth } from 'firebase/auth';
import { isLeft, isRight } from '@shared-commons/utils';
import { useAuthStore } from '@/auth';
import { Profile as ProfileDict } from '@/shared/i18n';

import { Profile } from '../types/profile';
import { update } from '../services/update-profile';
import { deleteProfile as handleDeleteProfile } from '../services/delete-profile';
import { ActionFeedbackType } from '../../ui/shared/module-actions-feedback';
import { ProfileLoading, useProfileStore } from '../stores/profile-store';
import { getProfile } from '../services/get-profile';
import { ProfileEventKeys, ProfileEvents } from '../events';
import { EventBus } from '@/shared/utils';

export const useProfile = (
  auth: Auth | null,
  dict?: ProfileDict,
  eventBus?: EventBus<ProfileEvents>,
) => {
  const {
    profile,
    setProfile,
    setIsLoading,
    setError,
    setActionFeedback,
    resetStore,
    resetLoadingState,
  } = useProfileStore();
  const { user } = useAuthStore();

  const accessToken = user?.token || '';

  //TODO @Leo token dentro user
  const messageMap = React.useMemo(
    () =>
      ({
        success: 'Operazione completata con successo',
        danger: "Si è verificato un errore durante l'operazione",
      }) as Partial<Record<ActionFeedbackType, string>>,
    [],
  );

  const refreshProfile = React.useCallback(
    async (input: { email: string; onSuccessCallback?: (profile: Profile) => void }) => {
      const loggedUser = user || input;
      if (!auth) return;

      const accessToken = await auth.currentUser!.getIdToken(true);

      const result = await getProfile(loggedUser?.email, accessToken)();
      if (isRight(result)) {
        setProfile(result.value);
        if (input?.onSuccessCallback) input?.onSuccessCallback(result.value);
      } else if (isLeft(result)) {
        const error = new Error(
          result.value?.message ||
            dict?.profile.account_refresh_error_message ||
            'Error has occurred',
        );
        setError(error);
        setIsLoading(null);
        return result.value;
      }
    },
    [messageMap, user?.email, setProfile, setError, setIsLoading, setActionFeedback, dict, auth],
  );

  const updateProfile = React.useCallback(
    async (profile: Profile) => {
      if (!auth) return;

      setError(null);
      setIsLoading(ProfileLoading.UPDATE);
      const accessToken = await auth.currentUser!.getIdToken(true);
      const profileResponse = await update(profile, accessToken)();
      if (isRight(profileResponse)) {
        setIsLoading(null);
        setActionFeedback({
          message: messageMap?.['success'],
          type: 'success',
        });
        // fetch newly updated profile in profile screen after update
        if (eventBus) eventBus.emit(ProfileEventKeys.profile.view, { email: user?.email || '' });
      } else if (isLeft(profileResponse)) {
        const error = new Error(
          profileResponse.value?.message ||
            dict?.profile.account_update_error_message ||
            'Error has occurred',
        );
        setError(error);
        setIsLoading(null);
        setActionFeedback({
          message: messageMap?.['danger'],
          type: 'danger',
        });
      }
    },
    [messageMap, user?.email, eventBus, setError, setIsLoading, setActionFeedback, dict, auth],
  );

  const deleteProfile = React.useCallback(async () => {
    if (!auth) return;
    setError(null);
    setIsLoading(ProfileLoading.DELETE);
    const accessToken = await auth.currentUser!.getIdToken(true);
    const result = await handleDeleteProfile(user?.email, user?.uid || '', accessToken)();
    if (isRight(result)) {
      setActionFeedback({
        message: messageMap?.['success'],
        type: 'success',
      });
      console.log('Profile has been deleted, redirecting to homepage and performing user logout');
      resetStore();
      if (eventBus) {
        eventBus.emit(ProfileEventKeys.profile.logout, undefined);
        eventBus.emit(ProfileEventKeys.redirect.to, '/');
      }
    } else if (isLeft(result)) {
      const error = new Error(
        result.value?.message || dict?.profile.account_delete_error_message || 'Error has occurred',
      );
      setError(error);
      setIsLoading(null);
      setActionFeedback({
        message: messageMap?.['danger'],
        type: 'danger',
      });
    }
  }, [
    messageMap,
    accessToken,
    resetStore,
    setProfile,
    setError,
    setIsLoading,
    setActionFeedback,
    user?.email,
    dict,
    resetStore,
    eventBus,
    auth,
  ]);

  return { setProfile, profile, refreshProfile, updateProfile, deleteProfile, resetLoadingState };
};
